import { useEffect, useState } from "react";
import {
  StyledCleanCacheText,
  StyledSettingsBtn,
} from "../../../../masterComponents/componentsByRole/actionButton/actionButtonByRole.style";
import { App, DatePicker } from "antd";
import {
  useFetchData as useExportLogsData,
  useFetchData as useLogDownloadUrlData,
} from "../../../../../hooks/useFetch/get";
import "./exportLogsBtn.css";
import type { ToUtcDayRangeType } from "./ExportLogsBtnType";
import { ExportLogsBtnContainer } from "./ExportLogsBtn.style";
import dayjs from "dayjs";
import { PHRASES_DICTIONARY } from "../../../../../entities/phrasesDictionary";

const toUtcDayRange: ToUtcDayRangeType = (dateYmd) => {
  const start = `${dateYmd}T00:00:00.000Z`;
  const end =
    dayjs(dateYmd).add(1, "day").format("YYYY-MM-DD") + "T00:00:00.000Z";
  return { start, end };
};

const ExportLogsBtn = () => {
  const { message: messageApi } = App.useApp();

  const [isPickingDate, setIsPickingDate] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const [
    fetchExportLogs,
    {
      isLoading: isLoadingExportLogs,
      data: exportLogsData,
      error: exportLogsError,
      clearError: clearExportLogsError,
      clearData: clearExportLogsData,
    },
  ] = useExportLogsData({
    query: "/audit-log/by-time/export/S3",
    selectData: (data) => data,
  });

  useEffect(() => {
    if (exportLogsError) {
      messageApi.error(exportLogsError.message);
      clearExportLogsError?.();
    }
  }, [exportLogsError, messageApi]);

  const [
    fetchLogDownloadUrl,
    {
      isLoading: isLoadingLogDownloadUrl,
      data: logDownloadUrlData,
      error: logDownloadUrlError,
      clearError: clearLogDownloadUrlError,
      clearData: clearLogDownloadUrlData,
    },
  ] = useLogDownloadUrlData({
    query: "/audit-log/export/download",
    selectData: (data) => data,
  });

  useEffect(() => {
    if (logDownloadUrlError) {
      messageApi.error(logDownloadUrlError.message);
      clearLogDownloadUrlError?.();
    }
  }, [logDownloadUrlError, messageApi]);

  useEffect(() => {
    if (exportLogsData?.file_key && !isLoadingExportLogs) {
      fetchLogDownloadUrl({ file_key: exportLogsData.file_key });
    }
  }, [isLoadingExportLogs]);

  useEffect(() => {
    if (logDownloadUrlData?.download_url && !isLoadingLogDownloadUrl) {
      window.open(logDownloadUrlData.download_url, "_blank");
      clearExportLogsData?.();
      clearLogDownloadUrlData?.();
    }
  }, [isLoadingLogDownloadUrl]);

  const handleExportClick = () => {
    setIsPickingDate(true);
    setSelectedDate(null);
  };

  const handleExecute = () => {
    if (!selectedDate) return;

    const payload = toUtcDayRange(selectedDate);
    fetchExportLogs(payload);

    setIsPickingDate(false);
    setSelectedDate(null);
  };

  const isLoading = isLoadingExportLogs || isLoadingLogDownloadUrl;

  return (
    <ExportLogsBtnContainer>
      {!isPickingDate && (
        <StyledSettingsBtn
          className="btn-color"
          loading={isLoading}
          onClick={handleExportClick}
        >
          Export Logs
        </StyledSettingsBtn>
      )}

      {isPickingDate && (
        <>
          <StyledSettingsBtn className="btn-color btn-dataPicker">
            <DatePicker
              style={{ width: 200 }}
              onChange={(_date, dateString) =>
                setSelectedDate((dateString as string) || null)
              }
              disabled={isLoading}
              allowClear={false}
              inputReadOnly
            />
            <StyledCleanCacheText>
              {PHRASES_DICTIONARY.PROMPTS_FOR_SETTINGS.choice_calendar_day_prompt}
            </StyledCleanCacheText>

            {selectedDate && (
              <StyledSettingsBtn
                className="btn-color"
                loading={isLoading}
                onClick={handleExecute}
                styles={{ width: "100%" }}
              >
                Execute
              </StyledSettingsBtn>
            )}
          </StyledSettingsBtn>
        </>
      )}
    </ExportLogsBtnContainer>
  );
};

export default ExportLogsBtn;
